import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, ShieldCheck, Phone, Mail, SearchCheck, FileSearch, Compass, Sparkles, FileCheck2, CircleDollarSign, ExternalLink } from "lucide-react";
import { GoldButton, GhostButton } from "../components/Buttons";
import { WhatsAppChatLink } from "../components/WhatsAppButton";
import ProtectionPromise from "../components/ProtectionPromise";
import Seo from "../components/Seo";
import IrsNewsPreview from "../components/IrsNewsPreview";
import { MaskedLines, Reveal, EASE } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { EditorialMarquee } from "../components/Marquee";
import { PROCESS, FAQS, AUDIENCES, MARQUEE_ITEMS, IMAGES, CONTACT, AUDIT_PROGRAM, AUDIT_BENEFITS, VALUE_PROPS, CLOSING_MESSAGE, TAX_BENEFIT_DISCLAIMER, AMENDMENT, EXPERIENCE, SERVICES_FEATURES, TAX_FORMS, SERVICE_BENEFITS, SERVICES_CTA, TAXPAYER_TOOLS } from "../data/content";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero-section">
            <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
                <img src={IMAGES.heroConsultant} alt="" className="w-full h-full object-cover object-[65%_center]" loading="eager" decoding="async" fetchPriority="high" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#111318] via-[#111318]/78 to-[#111318]/35" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111318] via-transparent to-[#111318]/60" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 w-full pt-32 pb-24">
                <motion.p
                    className="eyebrow"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
                >
                    Tax Filing That Puts You First
                </motion.p>

                <h1 className="font-display text-white text-[2rem] sm:text-6xl lg:text-[4.75rem] leading-[1.04] tracking-tight mt-7 max-w-4xl">
                    <MaskedLines
                        delay={0.3}
                        lines={[
                            <>
                                GET <em className="italic text-gold-gradient">EVERY DOLLAR</em>
                            </>,
                            "YOU DESERVE.",
                        ]}
                    />
                </h1>

                <motion.p
                    className="text-white/70 text-base sm:text-lg leading-relaxed mt-8 max-w-2xl"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
                >
                    Your hard-earned money matters. Freedom Tax Filer helps individuals, working professionals, gig
                    workers, and small-business owners navigate their taxes and identify eligible deductions and
                    credits, so they can make informed decisions about their tax returns.
                </motion.p>

                <motion.div
                    className="flex flex-col sm:flex-row gap-4 mt-10"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
                >
                    <GoldButton asChild data-testid="hero-get-started">
                        <Link to="/contact">
                            Get Started <ArrowRight className="h-4 w-4" />
                        </Link>
                    </GoldButton>
                    <GhostButton asChild data-testid="hero-amendment-review">
                        <Link to="/tax-amendment#amendment-form">
                            Free Tax Amendment Review
                        </Link>
                    </GhostButton>
                </motion.div>
            </motion.div>

            <motion.div
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50"
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
            >
                <ChevronDown className="h-6 w-6" />
            </motion.div>
        </section>
    );
};

const VALUE_SPANS = ["md:col-span-3", "md:col-span-3", "md:col-span-2", "md:col-span-2", "md:col-span-2"];

const ValueProps = () => (
    <section className="pb-24 lg:pb-32" data-testid="home-value-props">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <SectionHeading eyebrow="Why It Matters" title="Built to keep more of what's yours." />
            <div className="grid grid-cols-1 md:grid-cols-6 gap-5 mt-14">
                {VALUE_PROPS.map((p, i) => {
                    const Icon = p.icon;
                    return (
                        <Reveal key={p.title} delay={i * 0.06} className={VALUE_SPANS[i]}>
                            <div className="h-full bg-white border border-[#E7E4DC] shadow-sm p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#C8A45D]/60 hover:shadow-md" data-testid={`value-prop-card-${i}`}>
                                <div className="h-12 w-12 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center">
                                    <Icon className="h-5 w-5 text-[#8A6D2F]" />
                                </div>
                                <h3 className="font-display text-xl sm:text-2xl text-[#20242B] mt-7 leading-snug">{p.title}</h3>
                                <p className="text-[#737985] text-sm leading-relaxed mt-3">{p.text}</p>
                            </div>
                        </Reveal>
                    );
                })}
            </div>
            <Reveal delay={0.1}>
                <div className="mt-8 border border-[#C8A45D]/50 bg-[#C8A45D]/[0.09] px-8 sm:px-12 py-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="home-closing-message">
                    <p className="font-display italic text-xl sm:text-2xl text-[#20242B] leading-snug max-w-3xl">{CLOSING_MESSAGE}</p>
                    <GoldButton asChild data-testid="closing-discuss-cta" className="shrink-0">
                        <Link to="/contact">
                            Discuss My Tax Situation <ArrowRight className="h-4 w-4" />
                        </Link>
                    </GoldButton>
                </div>
                <p className="text-[#737985] text-xs leading-relaxed mt-6 max-w-3xl" data-testid="tax-benefit-disclaimer">{TAX_BENEFIT_DISCLAIMER}</p>
            </Reveal>
        </div>
    </section>
);

const FREE_BENEFITS = [
    {
        icon: SearchCheck,
        title: "Spot Potential Missed Benefits",
        text: "Check whether eligible deductions or credits may have been overlooked.",
    },
    {
        icon: FileSearch,
        title: "Identify Possible Errors",
        text: "Review your situation for potential reporting mistakes or missing information.",
    },
    {
        icon: Compass,
        title: "Understand Your Options",
        text: "Learn whether an amended return may be appropriate and what the next steps could involve.",
    },
];

const AmendmentTeaser = () => (
    <section className="section-pad bg-[#F3F1EC] border-y border-[#E7E4DC]" data-testid="home-amendment">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                <Reveal>
                    <div className="frame-gold">
                        <div className="bg-[#172337] p-4 sm:p-6 shadow-xl">
                            <img
                                src={IMAGES.amendment}
                                alt="A tax professional reviewing and signing a client's return"
                                className="w-full h-auto shadow-lg"
                                loading="lazy"
                                decoding="async"
                                data-testid="amendment-image"
                            />
                        </div>
                    </div>
                </Reveal>
                <div>
                    <SectionHeading
                        eyebrow="Tax Amendment Services"
                        title={<>Already Filed Your Taxes? You Could Be Leaving <em className="italic text-[#8A6D2F]">Money on the Table.</em></>}
                    />
                    <Reveal delay={0.08}>
                        <span className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#C8A45D] bg-[#C8A45D]/10 px-4 py-2 text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.18em] text-[#8A6D2F]" data-testid="amendment-free-label">
                            <Sparkles className="h-3.5 w-3.5" /> {AMENDMENT.freeLabel}
                        </span>
                        <p className="text-[#4A4F58] leading-relaxed mt-6 max-w-xl">{AMENDMENT.homeParagraph}</p>
                    </Reveal>
                    <Reveal delay={0.16} className="mt-9">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <GoldButton asChild data-testid="amendment-cta">
                                <Link to="/tax-amendment#amendment-form">
                                    {AMENDMENT.freeCta} <ArrowRight className="h-4 w-4" />
                                </Link>
                            </GoldButton>
                            <WhatsAppChatLink testid="amendment-teaser-whatsapp">Chat on WhatsApp</WhatsAppChatLink>
                        </div>
                        <p className="text-[#737985] text-xs mt-4" data-testid="amendment-reassurance">{AMENDMENT.reassurance}</p>
                    </Reveal>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 mt-16 pt-12 border-t border-[#E7E4DC]">
                {FREE_BENEFITS.map((b, i) => {
                    const Icon = b.icon;
                    return (
                        <Reveal key={b.title} delay={i * 0.08}>
                            <div className="border-t-2 border-[#C8A45D]/60 pt-6" data-testid={`amendment-benefit-${i}`}>
                                <span className="h-10 w-10 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center">
                                    <Icon className="h-4 w-4 text-[#8A6D2F]" />
                                </span>
                                <h3 className="font-display text-lg text-[#20242B] mt-4">{b.title}</h3>
                                <p className="text-[#737985] text-sm leading-relaxed mt-2">{b.text}</p>
                            </div>
                        </Reveal>
                    );
                })}
            </div>
            <Reveal delay={0.1}>
                <p className="text-[#737985] text-xs leading-relaxed mt-10 max-w-3xl">
                    Eligibility, filing deadlines, and possible outcomes depend on your individual circumstances and
                    applicable law. We don't guarantee refunds, tax savings, or IRS approval.
                </p>
            </Reveal>
        </div>
    </section>
);

const TrustExperience = () => (
    <section className="section-pad bg-[#172337] border-y border-white/10" data-testid="home-trust">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5">
                <Reveal>
                    <div className="relative border border-[#C8A45D]/40 bg-[#1C2B45] rounded-2xl px-8 py-12 sm:py-14 text-center overflow-hidden shadow-xl" data-testid="experience-badge">
                        <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-transparent via-[#C8A45D] to-transparent" />
                        <p className="text-[#E4D2A3] text-[11px] sm:text-xs uppercase tracking-[0.24em]">Established in {EXPERIENCE.established}</p>
                        <p className="font-display text-8xl sm:text-9xl text-gold-gradient leading-none mt-5" data-testid="experience-years">{EXPERIENCE.yearsOfService}</p>
                        <p className="mt-4 text-white/80 text-xs sm:text-sm uppercase tracking-[0.22em]">Years of Service</p>
                        <div className="gold-rule mx-auto mt-7" />
                        <p className="mt-7 text-[#C8A45D] text-[11px] sm:text-xs uppercase tracking-[0.2em]">Serving Clients Since {EXPERIENCE.established}</p>
                    </div>
                </Reveal>
            </div>
            <div className="lg:col-span-7">
                <SectionHeading
                    dark
                    eyebrow="Why Clients Trust Us"
                    title={<>Serving Clients Since {EXPERIENCE.established}. A Commitment to Your <em className="italic text-gold-gradient">Tax Confidence.</em></>}
                />
                <Reveal delay={0.1}>
                    <p className="text-white/65 leading-relaxed mt-6 max-w-3xl" data-testid="home-trust-paragraph">{EXPERIENCE.paragraph}</p>
                </Reveal>
            </div>
        </div>
    </section>
);

const TAX_PILLARS = [
    {
        icon: FileCheck2,
        title: "Thorough Tax Return Review",
        text: "We carefully review the information you provide, helping identify relevant deductions, credits, and potential issues based on your individual tax situation.",
    },
    {
        icon: CircleDollarSign,
        title: "Find Your Eligible Tax Benefits",
        text: "Our goal is to help you identify the deductions and credits you qualify for under applicable tax laws, so you can make informed decisions about your tax return.",
    },
    {
        icon: ShieldCheck,
        title: "Support Beyond Filing",
        text: "Get clear guidance on tax-related questions, eligible amendment options, and IRS notices. The assistance available depends on your service, eligibility, and applicable terms.",
    },
];

const TaxConfidence = () => (
    <section className="section-pad bg-[#F3F1EC] border-y border-[#E7E4DC]" data-testid="home-tax-confidence">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal className="text-center">
                <p className="text-[#8A6D2F] text-[12px] uppercase tracking-[0.2em]">The Freedom Tax Filer Standard</p>
                <h2 className="font-display text-3xl sm:text-5xl text-[#20242B] tracking-tight leading-tight mt-5 max-w-3xl mx-auto">
                    Tax Confidence Starts With the <em className="italic text-[#8A6D2F]">Right Team.</em>
                </h2>
                <div className="gold-rule mx-auto mt-6" />
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 sm:gap-10 lg:gap-14 mt-14 sm:mt-16">
                {TAX_PILLARS.map((p, i) => {
                    const Icon = p.icon;
                    return (
                        <Reveal key={p.title} delay={0.1 + i * 0.08}>
                            <div className="h-full text-center px-2" data-testid={`tax-confidence-pillar-${i}`}>
                                <span className="h-16 w-16 rounded-full border border-[#C8A45D]/60 bg-white shadow-sm flex items-center justify-center mx-auto">
                                    <Icon className="h-6 w-6 text-[#8A6D2F]" strokeWidth={1.75} />
                                </span>
                                <h3 className="font-display text-xl sm:text-2xl text-[#20242B] mt-6">{p.title}</h3>
                                <p className="text-[#737985] text-sm sm:text-[15px] leading-relaxed mt-3">{p.text}</p>
                            </div>
                        </Reveal>
                    );
                })}
            </div>
        </div>
    </section>
);

const AuditDefenseTeaser = () => (
    <section className="pb-24 lg:py-24 px-5 sm:px-8 bg-[#172337] border-y border-white/10" data-testid="home-audit-defense">
        <Reveal>
            <div className="mx-auto max-w-7xl relative border border-[#C8A45D]/40 bg-[#1C2B45] overflow-hidden rounded-2xl">
                <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#C8A45D] to-transparent" />
                <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="p-8 sm:p-12 lg:p-14">
                        <p className="eyebrow-dark">Audit Defense &amp; IRS Notice Assistance</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-6 leading-[1.1]">
                            We're here for you—<em className="italic text-gold-gradient">even after you file.</em>
                        </h2>
                        <p className="text-white/60 leading-relaxed mt-6">
                            Your relationship with Freedom Tax Filer doesn't have to end when your tax return is filed. We
                            provide ongoing tax notice and audit assistance for eligible returns prepared by our firm —
                            helping you understand IRS correspondence and determine the appropriate next steps.
                        </p>
                        <p className="text-white/80 font-semibold mt-9">Need Help With an IRS Letter?</p>
                        <div className="flex flex-col sm:flex-row gap-4 mt-4">
                            <GoldButton asChild data-testid="audit-teaser-cta">
                                <Link to="/contact">
                                    Contact Our Team <ArrowRight className="h-4 w-4" />
                                </Link>
                            </GoldButton>
                        </div>
                        <Link
                            to="/audit-defense"
                            data-testid="audit-teaser-terms-link"
                            className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.14em] text-[#E4D2A3] border-b border-[#C8A45D]/50 pb-1 mt-8 hover:border-[#E4D2A3] transition-colors duration-200"
                        >
                            Explore the full program & terms <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="border-t lg:border-t-0 lg:border-l border-white/10 bg-[#182640] p-8 sm:p-12 lg:p-14 rounded-b-2xl lg:rounded-b-none lg:rounded-r-2xl">
                        <ul className="space-y-7">
                            {AUDIT_BENEFITS.map((b, i) => {
                                const Icon = b.icon;
                                return (
                                    <li key={b.title} className="flex items-start gap-4" data-testid={`audit-teaser-benefit-${i}`}>
                                        <span className="h-10 w-10 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center shrink-0">
                                            <Icon className="h-4 w-4 text-[#E4D2A3]" />
                                        </span>
                                        <span>
                                            <span className="block text-white font-semibold text-sm sm:text-base">{b.title}</span>
                                            <span className="block text-white/55 text-sm leading-relaxed mt-1">{b.text}</span>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
                <div className="border-t border-[#C8A45D]/30 bg-[#C8A45D]/[0.08] px-8 sm:px-12 lg:px-14 py-7 flex flex-col sm:flex-row items-start gap-4" data-testid="audit-teaser-promise">
                    <ShieldCheck className="h-6 w-6 text-[#C8A45D] shrink-0 mt-0.5" />
                    <div>
                        <p className="text-white text-base sm:text-lg font-display italic">{AUDIT_PROGRAM.promise}</p>
                        <p className="text-[#E4D2A3] text-xs mt-2">For eligible returns prepared by Freedom Tax Filer, subject to the written service agreement.</p>
                    </div>
                </div>
            </div>
        </Reveal>
    </section>
);

const FAQPreview = () => (
    <section className="section-pad">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
                <SectionHeading eyebrow="Common Questions" title="Before you ask — good to know." />
                <Reveal delay={0.1}>
                    <p className="text-white/60 leading-relaxed mt-6 max-w-md">
                        The answers most people need first. Everything else is on the FAQ page — or one call away.
                    </p>
                    <GhostButton asChild className="mt-8" data-testid="home-faq-view-all">
                        <Link to="/faq">View All Questions</Link>
                    </GhostButton>
                </Reveal>
            </div>
            <div className="lg:col-span-7">
                <Reveal delay={0.15}>
                    <Accordion type="single" collapsible className="border-t border-[#E5E2DA]">
                        {FAQS.slice(0, 4).map((f, i) => (
                            <AccordionItem key={i} value={`home-faq-${i}`} className="border-b border-[#E5E2DA]" data-testid={`home-faq-item-${i}`}>
                                <AccordionTrigger className="font-display text-lg sm:text-xl text-[#20242B] hover:text-[#8A6D2F] hover:no-underline text-left py-6 px-0">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-[#737985] leading-relaxed pb-6 px-0">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>
            </div>
        </div>
    </section>
);

export default function Home() {
    return (
        <div data-testid="page-home">
            <Seo
                title="Tax Preparation Services in the U.S. | Freedom Tax Filer"
                description="Freedom Tax Filer prepares accurate federal and state tax returns for individuals, gig workers, and small businesses — with a free tax amendment review."
                path="/"
            />
            <Hero />
            <EditorialMarquee items={MARQUEE_ITEMS} />

            <AmendmentTeaser />

            <TrustExperience />

            <TaxConfidence />

            <ProtectionPromise />

            {/* Intro */}
            <section className="section-pad" data-testid="home-intro">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <SectionHeading eyebrow="The Firm" title={<>Tax preparation, done properly.</>} />
                        <Reveal delay={0.15} className="mt-12">
                            <div className="frame-gold">
                                <div className="relative overflow-hidden">
                                    <img src={IMAGES.preparer} alt="A tax preparer reviewing documents at their desk" className="w-full h-[280px] sm:h-[400px] object-cover saturate-[0.85] contrast-[1.05]" loading="lazy" decoding="async" data-testid="intro-image" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#111318]/60 via-transparent to-transparent" />
                                    <p className="absolute bottom-5 left-5 text-white/80 text-[11px] uppercase tracking-[0.22em]">Careful work, line by line.</p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7 space-y-6">
                        <Reveal delay={0.1}>
                            <p className="text-[#3D434D] text-lg leading-relaxed">
                                Freedom Tax Filer is a U.S. tax preparation service built around one idea: filing your taxes
                                should feel clear, considered, and yours. We prepare federal and state returns for everyday
                                earners, side-hustlers, and business owners across the country.
                            </p>
                            <p className="text-[#737985] leading-relaxed">
                                No jargon. No runaround. Just careful preparation, straight answers, and a process you can
                                follow from hello to filed — whether you have a single W-2 or a return that spans three states.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2} className="flex flex-wrap gap-3">
                            {AUDIENCES.map((a) => (
                                <span key={a} className="text-xs uppercase tracking-[0.14em] text-[#4A4F58] border border-[#D9D6CD] bg-white px-4 py-2">
                                    {a}
                                </span>
                            ))}
                        </Reveal>
                        <Reveal delay={0.25}>
                            <Link to="/about" data-testid="home-about-link" className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.14em] text-[#8A6D2F] border-b border-[#C8A45D]/50 pb-1 hover:border-[#C8A45D] transition-colors duration-200">
                                More about us <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            <ValueProps />

            {/* Services */}
            <section className="section-pad pt-0" data-testid="home-services">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <Reveal>
                            <p className="text-[#8A6D2F] text-[12px] uppercase tracking-[0.2em]">Tax Filing That Puts You First</p>
                            <h2 className="font-display text-3xl sm:text-5xl text-[#20242B] tracking-tight leading-tight mt-5">
                                Your Taxes. Handled <em className="italic text-[#8A6D2F]">With Care.</em>
                            </h2>
                            <p className="text-[#4A4F58] leading-relaxed mt-6">
                                From straightforward individual returns to self-employment income and more complex tax
                                situations, Freedom Tax Filer helps you navigate the filing process with personalized
                                service, clear communication, and support from qualified tax professionals.
                            </p>
                            <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-9">
                                <GoldButton asChild data-testid="services-get-started">
                                    <Link to="/contact">
                                        Get Started <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </GoldButton>
                                <Link
                                    to="/services"
                                    data-testid="services-explore-btn"
                                    className="inline-flex items-center justify-center h-12 px-8 rounded-lg border border-[#D9D6CD] bg-white text-[13px] font-semibold uppercase tracking-[0.1em] text-[#3D434D] hover:border-[#C8A45D] hover:text-[#8A6D2F] transition-colors duration-300 whitespace-nowrap"
                                >
                                    Explore Our Services
                                </Link>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="frame-gold">
                                <img
                                    src={IMAGES.servicesWorkspace}
                                    alt="An organized tax workspace with clear documents, a calculator, and a pen"
                                    className="w-full h-[300px] sm:h-[420px] object-cover"
                                    loading="lazy"
                                    decoding="async"
                                    data-testid="services-image"
                                />
                            </div>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
                        {SERVICES_FEATURES.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <Reveal key={f.title} delay={i * 0.06} className="h-full">
                                    <div className="h-full flex flex-col bg-white border border-[#E7E4DC] rounded-xl p-7 shadow-sm hover:border-[#C8A45D]/60 hover:shadow-lg transition-[border-color,box-shadow] duration-300" data-testid={`services-feature-${i}`}>
                                        <span className="h-11 w-11 rounded-lg border border-[#C8A45D]/50 bg-[#C8A45D]/10 flex items-center justify-center">
                                            <Icon className="h-5 w-5 text-[#8A6D2F]" />
                                        </span>
                                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#8A6D2F] mt-5">{f.name}</p>
                                        <h3 className="font-display text-xl text-[#20242B] mt-2">{f.title}</h3>
                                        <p className="text-[#737985] text-sm leading-relaxed mt-3 flex-1">{f.text}</p>
                                        <Link
                                            to={f.href}
                                            data-testid={`services-feature-link-${i}`}
                                            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] text-[#8A6D2F] border-b border-[#C8A45D]/50 pb-1 mt-5 hover:border-[#C8A45D] transition-colors duration-200 self-start"
                                        >
                                            {f.linkLabel} <ArrowRight className="h-3.5 w-3.5" />
                                        </Link>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <Reveal delay={0.05}>
                        <div className="mt-16 bg-[#F3F1EC] border border-[#E7E4DC] rounded-2xl p-8 sm:p-12" data-testid="tax-forms-panel">
                            <h3 className="font-display text-2xl sm:text-3xl text-[#20242B]">{TAX_FORMS.title}</h3>
                            <p className="text-[#737985] text-sm leading-relaxed mt-3 max-w-2xl">{TAX_FORMS.intro}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                                {TAX_FORMS.forms.map((form) => {
                                    const Icon = form.icon;
                                    return (
                                        <div key={form.name} className="bg-white border border-[#E7E4DC] rounded-lg p-5" data-testid={`tax-form-${form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                            <Icon className="h-4 w-4 text-[#8A6D2F]" />
                                            <p className="font-display text-[#20242B] mt-3">{form.name}</p>
                                            <p className="text-[#737985] text-[13px] leading-relaxed mt-1.5">{form.text}</p>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-[#737985] text-xs leading-relaxed mt-6">The forms needed depend on each customer's situation — not every customer needs every form.</p>
                        </div>
                    </Reveal>
                </div>

                <div className="mt-16 bg-[#172337] border-y border-white/10">
                    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                        {SERVICE_BENEFITS.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <Reveal key={b.title} delay={i * 0.08}>
                                    <div className="border-t-2 border-[#C8A45D]/60 pt-6" data-testid={`service-benefit-${i}`}>
                                        <Icon className="h-5 w-5 text-[#C8A45D]" />
                                        <h3 className="font-display text-lg text-white mt-4">{b.title}</h3>
                                        <p className="text-white/60 text-sm leading-relaxed mt-2">{b.text}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>

                <div className="mx-auto max-w-7xl px-5 sm:px-8 mt-16">
                    <Reveal>
                        <div className="relative border border-[#C8A45D]/40 bg-[#111318] rounded-2xl px-8 sm:px-14 py-14 overflow-hidden shadow-xl" data-testid="services-cta">
                            <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-transparent via-[#C8A45D] to-transparent" />
                            <h3 className="font-display text-3xl sm:text-4xl text-white leading-tight max-w-xl">
                                Let's Make Tax Season <em className="italic text-gold-gradient">Simpler.</em>
                            </h3>
                            <p className="text-white/60 leading-relaxed mt-4 max-w-xl">{SERVICES_CTA.text}</p>
                            <div className="mt-9">
                                <GoldButton asChild data-testid="services-cta-start">
                                    <Link to="/contact">
                                        {SERVICES_CTA.cta} <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </GoldButton>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Process */}
            <section className="section-pad bg-[#172337] border-y border-white/10" data-testid="home-process">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <SectionHeading dark eyebrow="The Process" title="Four steps from hello to filed." sub="A simple, transparent path — you always know what happens next." />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mt-16">
                        {PROCESS.map((p, i) => (
                            <Reveal key={p.num} delay={i * 0.1}>
                                <div className="relative border-t border-white/15 pt-8">
                                    <span className="absolute top-0 left-0 h-[2px] w-12 bg-[#C8A45D]" />
                                    <span className="font-display text-5xl text-gold-gradient">{p.num}</span>
                                    <h3 className="font-display text-xl text-white mt-5">{p.title}</h3>
                                    <p className="text-white/55 text-sm leading-relaxed mt-3">{p.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <AuditDefenseTeaser />

            <FAQPreview />

            <IrsNewsPreview />

            {/* CTA band */}
            <section className="pb-24 lg:pb-32 px-5 sm:px-8" data-testid="home-cta-band">
                <Reveal>
                    <div className="mx-auto max-w-7xl relative border border-[#C8A45D]/40 bg-[#111318] rounded-2xl px-8 sm:px-14 py-16 sm:py-20 overflow-hidden shadow-xl">
                        <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-transparent via-[#C8A45D] to-transparent" />
                        <p className="eyebrow-dark">Contact Us</p>
                        <h2 className="font-display text-3xl sm:text-5xl text-white mt-5 leading-tight max-w-2xl">
                            Let's discuss your tax preparation <em className="italic text-gold-gradient">needs.</em>
                        </h2>
                        <p className="text-white/60 leading-relaxed mt-5 max-w-xl">
                            Your hard-earned money matters. Tell us what you need — a short note or a call is all it takes
                            to get started.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 mt-10">
                            <GoldButton asChild data-testid="cta-band-get-started">
                                <Link to="/contact">Get Started</Link>
                            </GoldButton>
                            <GhostButton asChild data-testid="cta-band-refund-check">
                                <a href={TAXPAYER_TOOLS.cards[0].url} target="_blank" rel="noopener noreferrer external" aria-label="Check Your Refund on the official IRS website (opens in a new tab)">
                                    Check Your Refund <ExternalLink className="h-4 w-4" />
                                </a>
                            </GhostButton>
                            {CONTACT.phoneHref && (
                                <GhostButton asChild data-testid="cta-band-call">
                                    <a href={CONTACT.phoneHref}>
                                        <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                                    </a>
                                </GhostButton>
                            )}
                            <a href={CONTACT.emailHref} data-testid="cta-band-email" className="inline-flex items-center gap-2 text-[14px] text-[#E4D2A3] border-b border-[#C8A45D]/50 pb-1 mt-7 hover:border-[#E4D2A3] transition-colors duration-200">
                                <Mail className="h-4 w-4" /> {CONTACT.email}
                            </a>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
